import React, { createContext, useContext, useState, useEffect } from 'react';
import { DashboardSettings, DEFAULT_DASHBOARD_SETTINGS } from './types';

interface DashboardSettingsContextType {
  settings: DashboardSettings;
  updateSettings: (newSettings: Partial<DashboardSettings>) => void;
  resetSettings: () => void;
  isLoading: boolean;
}

const DashboardSettingsContext = createContext<DashboardSettingsContextType | undefined>(undefined);

export function useDashboardSettings() {
  const context = useContext(DashboardSettingsContext);
  if (context === undefined) {
    throw new Error('useDashboardSettings must be used within a DashboardSettingsProvider');
  }
  return context;
}

interface DashboardSettingsProviderProps {
  children: React.ReactNode;
  clientId?: string; // For client-specific settings
}

export function DashboardSettingsProvider({ 
  children, 
  clientId 
}: DashboardSettingsProviderProps) {
  const [settings, setSettings] = useState<DashboardSettings>(DEFAULT_DASHBOARD_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);

  // Storage key based on client ID for multi-tenant support
  const storageKey = clientId ? `dashboard-settings-${clientId}` : 'dashboard-settings';

  // Load settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        // Try to load from localStorage first
        const stored = localStorage.getItem(storageKey);
        if (stored) {
          const parsedSettings = JSON.parse(stored);
          // Merge with defaults to ensure all properties exist
          setSettings({ ...DEFAULT_DASHBOARD_SETTINGS, ...parsedSettings });
        }
        
        // TODO: In production, you might want to load from API
        // const apiSettings = await api.get(`/settings/dashboard/${clientId}`);
        // setSettings({ ...DEFAULT_DASHBOARD_SETTINGS, ...apiSettings.data });
        
      } catch (error) {
        console.error('Failed to load dashboard settings:', error);
        // Fall back to defaults
        setSettings(DEFAULT_DASHBOARD_SETTINGS);
      } finally {
        setIsLoading(false);
      }
    };

    loadSettings();
  }, [clientId, storageKey]);

  const updateSettings = (newSettings: Partial<DashboardSettings>) => {
    const updatedSettings = { ...settings, ...newSettings };
    setSettings(updatedSettings);
    
    // Save to localStorage
    try {
      localStorage.setItem(storageKey, JSON.stringify(updatedSettings));
      
      // TODO: In production, save to API
      // api.put(`/settings/dashboard/${clientId}`, updatedSettings);
      
    } catch (error) {
      console.error('Failed to save dashboard settings:', error);
    }
  };

  const resetSettings = () => {
    setSettings(DEFAULT_DASHBOARD_SETTINGS);
    try {
      localStorage.removeItem(storageKey);
      // TODO: In production, reset on API
      // api.delete(`/settings/dashboard/${clientId}`);
    } catch (error) {
      console.error('Failed to reset dashboard settings:', error);
    }
  };

  return (
    <DashboardSettingsContext.Provider 
      value={{ 
        settings, 
        updateSettings, 
        resetSettings, 
        isLoading 
      }}
    >
      {children}
    </DashboardSettingsContext.Provider>
  );
}
